import './gm-cart-upsell-v6.js';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const GIFT_VARIANT_ID = 43242063495286;
const GIFT_THRESHOLD = 50000;
const SYNC_DELAY = 140;

let timer = null;
let syncing = false;
let rerun = false;
let giftUiTimer = null;

function hideDiscountField() {
  if (document.getElementById('gm-hide-cart-discount')) return;
  const style = document.createElement('style');
  style.id = 'gm-hide-cart-discount';
  style.textContent = '.cart-discount{display:none!important}';
  document.head.appendChild(style);
}

function installGiftUiStyles() {
  if (document.getElementById('gm-free-gift-ui-styles')) return;

  const style = document.createElement('style');
  style.id = 'gm-free-gift-ui-styles';
  style.textContent = `
    .gm-reward-bar {
      --gm-purple: #8a0f6a !important;
      --gm-purple-soft: rgb(138 15 106 / 0.14) !important;
    }

    .gm-reward-bar__icon {
      border-color: rgb(138 15 106 / 0.28) !important;
    }

    .gm-free-gift-line cart-remove-button,
    .gm-free-gift-line .cart-remove-button,
    .gm-free-gift-line .cart-items__remove,
    .gm-free-gift-line [data-cart-remove],
    .gm-free-gift-line [data-remove],
    .gm-free-gift-line quantity-selector,
    .gm-free-gift-line .quantity-selector,
    .gm-free-gift-line .cart-items__quantity,
    .gm-free-gift-line [class*='quantity'],
    .gm-free-gift-line button[name='minus'],
    .gm-free-gift-line button[name='plus'],
    .gm-free-gift-line .cart-items__price,
    .gm-free-gift-line .cart-items__total,
    .gm-free-gift-line .cart-items__unit-price-wrapper,
    .gm-free-gift-line .cart-item__discounted-prices,
    .gm-free-gift-line [class*='price'],
    .gm-free-gift-line .money,
    .gm-free-gift-line .cart-items__variants-wrapper,
    .gm-free-gift-line .cart-items__properties,
    .gm-free-gift-line .cart-items__discounts,
    .gm-free-gift-line .discounts,
    .gm-free-gift-line .cart-disclosure,
    .gm-free-gift-line [aria-label*='Remove'],
    .gm-free-gift-line [aria-label*='remove'] {
      display: none !important;
    }

    .gm-free-gift-line .cart-items__title,
    .gm-free-gift-line .cart-item__name {
      pointer-events: none !important;
      cursor: default !important;
    }
  `;
  document.head.appendChild(style);
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
  return response.json();
}

function eligibleTotal(cart) {
  return cart.items
    .filter((item) => item.variant_id !== GIFT_VARIANT_ID)
    .reduce((sum, item) => sum + item.final_line_price, 0);
}

function sectionIds() {
  return [...new Set(
    [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((element) => element.dataset.sectionId)
      .filter(Boolean)
  )];
}

function decorateGiftLine(cart) {
  document.querySelectorAll('.gm-free-gift-line').forEach((element) => {
    element.classList.remove('gm-free-gift-line');
  });

  const giftLine = cart.items.find((item) => item.variant_id === GIFT_VARIANT_ID);
  if (!giftLine) return;

  const escapedKey = window.CSS?.escape
    ? window.CSS.escape(giftLine.key)
    : giftLine.key.replace(/["\\]/g, '\\$&');

  document.querySelectorAll(`[data-key="${escapedKey}"]`).forEach((row) => {
    row.classList.add('gm-free-gift-line');

    const title = row.querySelector('.cart-items__title, .cart-item__name');
    if (title) {
      if (title.textContent.trim() !== 'Free gift') title.textContent = 'Free gift';
      title.removeAttribute('href');

      const details = title.closest('.cart-items__details');
      if (details) {
        [...details.children].forEach((child) => {
          if (child !== title && !child.contains(title)) child.hidden = true;
        });
      }
    }
  });
}

function queueGiftUi(delay = 40) {
  if (giftUiTimer !== null) clearTimeout(giftUiTimer);
  giftUiTimer = setTimeout(async () => {
    giftUiTimer = null;
    try {
      decorateGiftLine(await fetchCart());
    } catch (error) {
      console.error('[Green Mist gift UI]', error);
    }
  }, delay);
}

async function updateGift(endpoint, payload, action, line) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(new CartLinesUpdateEvent({
    action,
    context: 'cart',
    lines: [line],
    promise: deferred.promise,
  }));

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const result = await response.json();
    if (!response.ok || result.status || result.errors) {
      throw new Error(result.description || result.message || result.errors || 'Gift update failed');
    }

    const cart = await fetchCart();
    decorateGiftLine(cart);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: result.sections || {},
        items: cart.items,
        itemCount: cart.item_count,
        source: 'gm-free-gift',
        didError: false,
      },
    });
    queueGiftUi(80);
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

async function syncGift() {
  if (syncing) {
    rerun = true;
    return;
  }

  syncing = true;
  try {
    const cart = await fetchCart();
    decorateGiftLine(cart);
    const giftLine = cart.items.find((item) => item.variant_id === GIFT_VARIANT_ID);
    const qualifies = eligibleTotal(cart) >= GIFT_THRESHOLD;
    const ids = sectionIds();
    const sections = ids.length
      ? { sections: ids.join(','), sections_url: window.location.pathname }
      : {};

    if (qualifies && !giftLine) {
      await updateGift(
        Theme.routes.cart_add_url,
        { items: [{ id: GIFT_VARIANT_ID, quantity: 1, properties: { _free_gift: 'true' } }], ...sections },
        'add',
        { merchandiseId: String(GIFT_VARIANT_ID), quantity: 1 }
      );
    } else if (!qualifies && giftLine) {
      await updateGift(
        Theme.routes.cart_change_url,
        { id: giftLine.key, quantity: 0, ...sections },
        'remove',
        { id: giftLine.key, quantity: 0 }
      );
    } else if (qualifies && giftLine && giftLine.quantity !== 1) {
      await updateGift(
        Theme.routes.cart_change_url,
        { id: giftLine.key, quantity: 1, ...sections },
        'update',
        { id: giftLine.key, quantity: 1 }
      );
    }
  } catch (error) {
    console.error('[Green Mist free gift]', error);
  } finally {
    syncing = false;
    if (rerun) {
      rerun = false;
      void syncGift();
    }
  }
}

function queueSync(delay = SYNC_DELAY) {
  if (timer !== null) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    void syncGift();
  }, delay);
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.promise) {
    event.promise
      .then(({ detail }) => {
        if (!detail?.didError && detail?.source !== 'gm-free-gift') queueSync();
        queueGiftUi(80);
      })
      .catch(() => {});
  } else {
    queueSync();
  }
});

installGiftUiStyles();
hideDiscountField();

if (document.body) {
  new MutationObserver(() => queueGiftUi()).observe(document.body, {
    childList: true,
    subtree: true,
  });
}

window.addEventListener('pageshow', () => {
  queueSync(0);
  queueGiftUi(0);
});
queueSync(0);
queueGiftUi(0);
